import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/cn";

/**
 * Badge primitive
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.2
 *
 * Rules baked in:
 *   - Single size: `px-2.5 py-0.5 text-xs font-semibold`, `rounded-md`,
 *     `whitespace-nowrap`. The legacy app uses `text-[10px]` for inline tag
 *     clusters that must fit on one row — apply that via `className` only,
 *     never as a separate size variant.
 *   - Always `hover-elevate`. Never add `hover:bg-*` to a `<Badge>`.
 *   - `outline` variant uses the theme-aware `--badge-outline` token via
 *     inline border-color so light/dark mode flip the rim alpha automatically.
 *   - Coloured badges (amber/violet/etc.) follow the QboSyncBadge pattern:
 *     pair `bg-{color}-100` with `dark:bg-{color}-900/30`,
 *     `text-{color}-800` with `dark:text-{color}-400`,
 *     `border-{color}-200` with `dark:border-{color}-900/50`.
 *     Pass those via `className` while keeping `variant="outline"`.
 *
 * Use `className="no-default-active-elevate"` on the rare badge that must
 * suppress the press-overlay (inline tag clusters where the host already
 * captures the click).
 */
const badgeVariants = cva(
  [
    "inline-flex items-center gap-1 rounded-md px-2.5 py-0.5",
    "text-xs font-semibold whitespace-nowrap",
    "hover-elevate",
    "transition-colors focus:outline-none focus:ring-1 focus:ring-ring",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow-xs",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow-xs",
        outline:
          "border bg-transparent text-foreground shadow-xs [border-color:var(--badge-outline)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
